import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';
import { RefreshCw, Search } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

export default function GA4CustomDimensions() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('definitions');
  const [selectedDimKey, setSelectedDimKey] = useState('');
  const [rangeDays, setRangeDays] = useState('7');
  const [topN, setTopN] = useState(20);
  const [valuesLoading, setValuesLoading] = useState(false);
  const [valuesError, setValuesError] = useState('');
  const [values, setValues] = useState([]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const display = String(item.displayName || '').toLowerCase();
      const param = String(item.parameterName || '').toLowerCase();
      const desc = String(item.description || '').toLowerCase();
      const scope = String(item.scope || '').toLowerCase();
      return display.includes(q) || param.includes(q) || desc.includes(q) || scope.includes(q);
    });
  }, [items, query]);

  const fetchDimensions = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/google-analytics/ga4/custom-dimensions`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      setItems(list);
      setLastUpdated(new Date());
      if (!selectedDimKey && list.length > 0) {
        const first = list[0];
        const key = `${first.scope || 'EVENT'}::${first.parameterName || ''}`;
        setSelectedDimKey(key);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load custom dimensions');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchValues = async () => {
    if (!selectedBrand?.id || !selectedDimKey) return;
    const [scope, parameterName] = selectedDimKey.split('::');
    if (!parameterName) return;
    try {
      setValuesLoading(true);
      setValuesError('');
      setValues([]);
      const url = new URL(`${API_BASE}/api/google-analytics/ga4/custom-dimensions/values`);
      url.searchParams.set('dimension', parameterName);
      url.searchParams.set('scope', scope);
      url.searchParams.set('days', rangeDays);
      url.searchParams.set('limit', String(topN || 20));
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data?.data) ? data.data : [];
      setValues(list);
    } catch (e) {
      setValuesError(e?.message || 'Failed to load dimension values');
      setValues([]);
    } finally {
      setValuesLoading(false);
    }
  };

  useEffect(() => {
    fetchDimensions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
              <img src="/GA4logoicon.svg" alt="GA4" className="w-6 h-6" />
            </div>
          <div>
            <h1 className="text-2xl tracking-tight">GA4 Custom Dimensions</h1>
            <p className="text-sm text-gray-500">
              Live list of custom dimensions configured in your GA4 property.
            </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setActiveTab('definitions')}
                className={`px-3 py-2 text-sm ${activeTab === 'definitions' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Definitions
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('values')}
                className={`px-3 py-2 text-sm ${activeTab === 'values' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Dimension Values
              </button>
            </div>
            <button
              type="button"
              onClick={fetchDimensions}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        {activeTab === 'definitions' ? (
          <>
            <div className="p-4 bg-white border-b">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, parameter, scope, or description..."
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Not updated yet'}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="px-6 py-16 bg-white text-center text-gray-500">Loading custom dimensions...</div>
            ) : error ? (
              <div className="px-6 py-16 bg-white text-center text-red-600">{error}</div>
            ) : filteredItems.length === 0 ? (
              <div className="px-6 py-16 bg-white text-center text-gray-500">
                No custom dimensions found.
              </div>
            ) : (
              <div className="px-6 py-4 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Display Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Parameter
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Scope
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ads Personalization
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredItems.map((item) => (
                        <tr key={item.name || `${item.parameterName}-${item.displayName}`}>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {item.displayName || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {item.parameterName || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {item.scope || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.description || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {typeof item.disallowAdsPersonalization === 'boolean'
                              ? (item.disallowAdsPersonalization ? 'Disabled' : 'Enabled')
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="p-4 bg-white border-b">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Custom Dimension</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    value={selectedDimKey}
                    onChange={(e) => setSelectedDimKey(e.target.value)}
                  >
                    {(items || []).map((item) => {
                      const key = `${item.scope || 'EVENT'}::${item.parameterName || ''}`;
                      const label = `${item.displayName || item.parameterName} • ${item.scope || 'EVENT'}`;
                      return (
                        <option key={key} value={key}>{label}</option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Range</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    value={rangeDays}
                    onChange={(e) => setRangeDays(e.target.value)}
                  >
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Top N</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={topN}
                    onChange={(e) => setTopN(Number(e.target.value || 20))}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={fetchValues}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                    disabled={valuesLoading || !selectedDimKey}
                  >
                    {valuesLoading ? 'Loading...' : 'Load Values'}
                  </button>
                </div>
              </div>
            </div>

            {valuesLoading ? (
              <div className="px-6 py-16 bg-white text-center text-gray-500">Loading values...</div>
            ) : valuesError ? (
              <div className="px-6 py-16 bg-white text-center text-red-600">{valuesError}</div>
            ) : values.length === 0 ? (
              <div className="px-6 py-16 bg-white text-center text-gray-500">
                No values found. Click "Load Values".
              </div>
            ) : (
              <div className="px-6 py-4 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Value
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Event Count
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {values.map((row, idx) => (
                        <tr key={`${row.value}-${idx}`}>
                          <td className="px-4 py-3 text-sm text-gray-900">{row.value || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">
                            {Number(row.eventCount || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
